
import React, { useState } from 'react';
import { useAudio } from '../state/AudioContext';
import { PlayableItem } from '../domain/types';

interface ProductAudioPlayerProps {
    item: PlayableItem;
    isCompact?: boolean;
}

export const ProductAudioPlayer: React.FC<ProductAudioPlayerProps> = ({ item, isCompact = false }) => {
    const { currentItem, isPlaying, isPaused, play, rate, setPlaybackRate } = useAudio();
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);

    const isThisActive = currentItem?.id === item.id;
    const isThisPlaying = isThisActive && isPlaying;
    const isThisPaused = isThisActive && isPaused;

    const toggleAudio = () => {
        play(item);
    };

    const handleSpeedChange = (newRate: number) => {
        setPlaybackRate(newRate);
        setShowSpeedMenu(false);
    };

    if (isCompact) {
        return (
            <div className="relative">
                <button 
                    onClick={toggleAudio} 
                    className={`flex size-9 items-center justify-center rounded-full transition-all active:scale-90 border shadow-sm ${
                        isThisPlaying 
                        ? 'bg-primary border-primary text-white animate-pulse' 
                        : isThisPaused
                        ? 'bg-yellow-500 border-yellow-500 text-white'
                        : 'bg-black/10 dark:bg-white/10 border-white/10 text-text-main dark:text-white hover:bg-black/20'
                    }`}
                >
                    <span className="material-symbols-outlined text-[20px]">
                        {isThisPlaying ? 'pause_circle' : isThisPaused ? 'play_circle' : 'headphones'}
                    </span>
                </button>
                {isThisActive && (
                    <button 
                        onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                        className="absolute -top-1 -right-1 size-4 bg-white text-black text-[8px] font-black rounded-full flex items-center justify-center border border-primary shadow-sm"
                    >
                        {rate}x
                    </button>
                )}
                {showSpeedMenu && (
                    <div className="absolute top-10 right-0 bg-black/95 backdrop-blur-xl border border-white/20 rounded-xl p-1 flex flex-col gap-1 w-20 shadow-2xl z-[110]">
                        {[0.75, 1.0, 1.5, 2.0].map(s => (
                            <button key={s} onClick={() => handleSpeedChange(s)} className={`text-[10px] font-bold py-1.5 rounded-lg ${rate === s ? 'bg-primary text-white' : 'text-white hover:bg-white/10'}`}>{s}x</button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative z-[100]">
            <div className={`flex items-center rounded-full transition-all duration-300 border ${isThisPlaying ? 'bg-primary border-primary text-white pl-1 pr-3 shadow-lg shadow-primary/30' : isThisPaused ? 'bg-yellow-500 border-yellow-500 text-white pl-1 pr-3 shadow-lg' : 'bg-black/20 dark:bg-white/10 backdrop-blur-md border-white/10 text-white hover:bg-black/40 pr-3 pl-1'}`}>
                <button onClick={toggleAudio} className="w-9 h-9 flex items-center justify-center rounded-full active:scale-90 transition-transform">
                    <span className={`material-symbols-outlined ${isThisPlaying ? 'animate-pulse' : ''}`}>{isThisPlaying ? 'pause_circle' : isThisPaused ? 'play_circle' : 'headphones'}</span>
                </button>
                <button onClick={toggleAudio} className="text-[10px] font-bold uppercase tracking-wide mr-2 text-left min-w-[70px]">{isThisPlaying ? 'Pausar' : isThisPaused ? 'Reanudar' : 'Escuchar'}</button>
                <div className={`w-px h-4 mx-1 ${isThisActive ? 'bg-white/30' : 'bg-white/20'}`}></div>
                <button onClick={() => setShowSpeedMenu(!showSpeedMenu)} className="text-[10px] font-bold w-6 text-center hover:scale-110 transition-transform">{rate}x</button>
            </div>

            {showSpeedMenu && (
                <div className="absolute top-12 right-0 bg-black/95 backdrop-blur-xl border border-white/20 rounded-xl p-1.5 flex flex-col gap-1 w-24 shadow-2xl animate-fade-in z-[110]">
                    <p className="text-[9px] text-gray-400 text-center font-bold uppercase mb-1 border-b border-white/10 pb-1">Velocidad</p>
                    {[0.75, 1.0, 1.5, 2.0].map(s => (
                        <button key={s} onClick={() => handleSpeedChange(s)} className={`text-[11px] font-bold py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${rate === s ? 'bg-primary text-white' : 'text-white hover:bg-white/10'}`}>{s}x {rate === s && <span className="material-symbols-outlined text-[12px]">check</span>}</button>
                    ))}
                </div>
            )}
        </div>
    );
};
